// src/pages/NotificationsPage.js
// Admin CRUD for the notification engine.
// Backed by /api/notifications/* — see backend/routes/notifications.js.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../scripts/api";
import Modal from "../components/Modal";
import {
  Bell, Plus, Edit2, Trash2, Send, X, AlertCircle, CheckCircle2, Settings,
} from "lucide-react";

export default function NotificationsPage() {
  const [events, setEvents] = useState([]);
  const [knownKeys, setKnownKeys] = useState([]);
  const [mg, setMg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg,  setMsg]  = useState("");
  const [editing, setEditing] = useState(null);      // null | {id?, event_key, name, ...}
  const [creating, setCreating] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [log, setLog] = useState([]);

  const load = useCallback(async () => {
    setBusy(true); setMsg("");
    try {
      const [eventsRes, keysRes, mgRes] = await Promise.all([
        api.get("/notifications/events"),
        api.get("/notifications/event-keys"),
        api.get("/notifications/mailgun/status"),
      ]);
      setEvents(eventsRes.data?.events || []);
      setKnownKeys(keysRes.data?.known || []);
      setMg(mgRes.data || null);
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to load notifications.");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadLog = async () => {
    try {
      const { data } = await api.get("/notifications/log");
      setLog(data?.log || []);
      setLogOpen(true);
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to load log.");
    }
  };

  const toggleEnabled = async (ev) => {
    try {
      await api.patch(`/notifications/events/${ev.id}`, { enabled: ev.enabled ? 0 : 1 });
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Toggle failed.");
    }
  };

  const onDelete = async (ev) => {
    if (!window.confirm(`Delete notification "${ev.name}"? This also removes its recipients.`)) return;
    try {
      await api.delete(`/notifications/events/${ev.id}`);
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Delete failed.");
    }
  };

  const onResetDefaults = async (ev) => {
    if (!window.confirm(`Reset "${ev.name}" subject / text / HTML templates to the built-in defaults?\n\nRecipients and enable state are not affected.`)) return;
    try {
      await api.post(`/notifications/events/${ev.id}/reset-defaults`);
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Reset failed.");
    }
  };

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900 }}>
            <Bell size={22} style={{ verticalAlign: -4, marginRight: 8 }} />
            Notifications
          </h2>
          <div className="muted" style={{ marginTop: 4 }}>
            Configure which events send emails, what they say, and who gets them.
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn-outline" onClick={loadLog}>Recent Log</button>
          <button className="btn" onClick={() => setCreating(true)}>
            <Plus size={16} style={{ marginRight: 6, verticalAlign: -2 }} />
            New Event
          </button>
        </div>
      </div>

      {/* MailGun status banner */}
      {mg && (
        <div
          className="card"
          style={{
            marginTop: 10,
            background: mg.configured ? "#dcfce7" : "#fef3c7",
            border: `1px solid ${mg.configured ? "#86efac" : "#fde68a"}`,
            color: mg.configured ? "#166534" : "#92400e",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {mg.configured ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800 }}>
                {mg.configured ? "Email notifications are configured" : "MailGun is NOT configured"}
              </div>
              <div style={{ fontSize: 13, marginTop: 2 }}>
                {mg.configured
                  ? <>Domain: <code>{mg.domain}</code> · Region: <code>{mg.region}</code> · From: <code>{mg.from_default}</code>{mg.sandbox_allowlist?.length ? <> · Sandbox allow-list: <code>{mg.sandbox_allowlist.join(", ")}</code></> : null}</>
                  : <>Set <code>MAILGUN_API_KEY</code> and <code>MAILGUN_DOMAIN</code> in the backend .env, then restart the server.</>}
              </div>
            </div>
          </div>
        </div>
      )}

      {msg && <div className="card" style={{ marginTop: 10, color: "#dc2626" }}>{msg}</div>}

      <div className="card" style={{ marginTop: 14 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Events</div>
        {busy && <div className="muted">Loading…</div>}
        {!busy && events.length === 0 && <div className="muted">No events configured.</div>}

        <div style={{ display: "grid", gap: 10 }}>
          {events.map((ev) => (
            <div key={ev.id} className="card" style={{ background: "rgba(255,255,255,0.02)", padding: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ fontWeight: 800, fontSize: 16 }}>{ev.name}</div>
                    <code style={{ fontSize: 12, opacity: 0.7 }}>{ev.event_key}</code>
                    <Pill color={ev.enabled ? "#16a34a" : "#6b7280"} bg={ev.enabled ? "#dcfce7" : "#f3f4f6"}>
                      {ev.enabled ? "ENABLED" : "DISABLED"}
                    </Pill>
                    <Pill color="#2563eb" bg="#dbeafe">
                      {ev.recipient_count} recipient{ev.recipient_count === 1 ? "" : "s"}
                    </Pill>
                  </div>
                  {ev.description && <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{ev.description}</div>}
                  <div className="muted" style={{ fontSize: 12, marginTop: 6, fontFamily: "monospace" }}>
                    Subject: {ev.subject_template}
                  </div>
                </div>
                <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                  <button className="btn-outline" onClick={() => toggleEnabled(ev)} title={ev.enabled ? "Disable" : "Enable"}>
                    {ev.enabled ? "Disable" : "Enable"}
                  </button>
                  <button className="btn-outline" onClick={() => setEditing(ev)}>
                    <Edit2 size={14} style={{ marginRight: 4, verticalAlign: -2 }} />
                    Edit
                  </button>
                  <button className="btn-outline" onClick={() => onResetDefaults(ev)} title="Reset templates to built-in defaults">
                    Reset Templates
                  </button>
                  <button className="btn-outline" onClick={() => onDelete(ev)} style={{ color: "#dc2626" }}>
                    <Trash2 size={14} style={{ marginRight: 4, verticalAlign: -2 }} />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {(editing || creating) && (
        <EventEditor
          event={editing}
          knownKeys={knownKeys}
          existingKeys={events.map(e => e.event_key)}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }}
        />
      )}

      {logOpen && (
        <Modal onClose={() => setLogOpen(false)}>
          <h3 style={{ marginTop: 0 }}>Recent Notification Log</h3>
          {log.length === 0 && <div className="muted">No log entries yet.</div>}
          <div style={{ maxHeight: 480, overflow: "auto", display: "grid", gap: 6 }}>
            {log.map((r) => (
              <div key={r.id} className="card" style={{ padding: 10, background: r.status === "sent" ? "#f0fdf4" : "#fef2f2" }}>
                <div style={{ fontSize: 12, display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <span>
                    <code>{r.event_key}</code> → {r.recipient}
                  </span>
                  <span style={{ fontWeight: 700, color: r.status === "sent" ? "#15803d" : "#b91c1c" }}>
                    {r.status.toUpperCase()}
                  </span>
                </div>
                {r.subject && <div style={{ fontSize: 13, marginTop: 4, fontWeight: 600 }}>{r.subject}</div>}
                {r.error && <div style={{ fontSize: 12, marginTop: 4, color: "#b91c1c" }}>{r.error}</div>}
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>{r.sent_at}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12, textAlign: "right" }}>
            <button className="btn-outline" onClick={() => setLogOpen(false)}>Close</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Pill({ color, bg, children }) {
  return (
    <span style={{
      background: bg, color, borderRadius: 99, padding: "2px 10px",
      fontSize: 10, fontWeight: 800, letterSpacing: 0.4,
    }}>
      {children}
    </span>
  );
}

// ── Editor modal with tabs: Details | Recipients | Test ─────────────────────
function EventEditor({ event, knownKeys, existingKeys, onClose, onSaved }) {
  const isNew = !event?.id;
  const [tab, setTab] = useState(isNew ? "details" : "details");

  // Details form
  const [eventKey, setEventKey]     = useState(event?.event_key || "");
  const [name, setName]             = useState(event?.name || "");
  const [description, setDescription] = useState(event?.description || "");
  const [enabled, setEnabled]       = useState(Boolean(event?.enabled));
  const [subjectTpl, setSubjectTpl] = useState(event?.subject_template || "");
  const [bodyTpl, setBodyTpl]       = useState(event?.body_template || "");
  const [htmlTpl, setHtmlTpl]       = useState(event?.html_template || "");
  const [sendMode, setSendMode]     = useState(event?.send_mode || "group");
  const [htmlPreview, setHtmlPreview] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [busy, setBusy]             = useState(false);
  const [err, setErr]               = useState("");

  // Recipients
  const [recipients, setRecipients] = useState([]);
  const [recType, setRecType]       = useState("email");
  const [recVal, setRecVal]         = useState("");
  const [recLabel, setRecLabel]     = useState("");
  const [drivers, setDrivers]       = useState([]);

  // Test send
  const [testTo, setTestTo]         = useState("");
  const [testResult, setTestResult] = useState(null);

  const loadRecipients = useCallback(async () => {
    if (isNew) return;
    try {
      const { data } = await api.get(`/notifications/events/${event.id}/recipients`);
      setRecipients(data?.recipients || []);
    } catch (_) {}
  }, [event?.id, isNew]);

  useEffect(() => { loadRecipients(); }, [loadRecipients]);
  useEffect(() => {
    api.get("/driver").then(({ data }) => setDrivers(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const selectKnownKey = async (key) => {
    const known = knownKeys.find(k => k.event_key === key);
    if (!known) return;
    setEventKey(known.event_key);
    setName(known.name);
    setDescription(known.description || "");
    // Pull the seed default templates so the new event starts with nice HTML
    try {
      const { data } = await api.get(`/notifications/event-keys/${encodeURIComponent(key)}/defaults`);
      setSubjectTpl(data.subject_template || "");
      setBodyTpl(data.body_template || "");
      setHtmlTpl(data.html_template || "");
    } catch (_) {}
  };

  const loadDefaults = async () => {
    if (!eventKey) return;
    if (!window.confirm("Replace subject / text / HTML templates with the built-in defaults?")) return;
    try {
      const { data } = await api.get(`/notifications/event-keys/${encodeURIComponent(eventKey)}/defaults`);
      setSubjectTpl(data.subject_template || "");
      setBodyTpl(data.body_template || "");
      setHtmlTpl(data.html_template || "");
    } catch (e) {
      setErr(e?.response?.data?.error || "No defaults available for this event_key.");
    }
  };

  const runPreview = async () => {
    try {
      const { data } = await api.post("/notifications/preview", { template: htmlTpl });
      setHtmlPreview(data.rendered || "");
      setShowPreview(true);
    } catch (e) {
      setErr(e?.response?.data?.error || "Preview failed");
    }
  };

  const save = async () => {
    setErr(""); setBusy(true);
    try {
      if (isNew) {
        if (!eventKey.trim()) throw new Error("event_key required");
        if (existingKeys.includes(eventKey.trim())) throw new Error("event_key already exists");
        await api.post("/notifications/events", {
          event_key: eventKey.trim(),
          name: name.trim() || eventKey.trim(),
          description,
          enabled: enabled ? 1 : 0,
          subject_template: subjectTpl,
          body_template: bodyTpl,
          html_template: htmlTpl || null,
          send_mode: sendMode,
        });
      } else {
        await api.patch(`/notifications/events/${event.id}`, {
          name: name.trim(),
          description,
          enabled: enabled ? 1 : 0,
          subject_template: subjectTpl,
          body_template: bodyTpl,
          html_template: htmlTpl || null,
          send_mode: sendMode,
        });
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const addRecipient = async () => {
    if (!recVal.trim()) return;
    try {
      await api.post(`/notifications/events/${event.id}/recipients`, {
        recipient_type: recType, recipient_value: recVal.trim(), label: recLabel || null,
      });
      setRecVal(""); setRecLabel("");
      loadRecipients();
    } catch (e) {
      setErr(e?.response?.data?.error || "Add failed");
    }
  };

  const removeRecipient = async (id) => {
    if (!window.confirm("Remove this recipient?")) return;
    await api.delete(`/notifications/recipients/${id}`);
    loadRecipients();
  };

  const runTest = async () => {
    setTestResult(null);
    try {
      const payload = testTo.trim() ? { to: testTo.trim() } : {};
      const { data } = await api.post(`/notifications/events/${event.id}/test`, payload);
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.error || e.message });
    }
  };

  return (
    <Modal onClose={onClose}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0 }}>{isNew ? "New Notification Event" : `Edit: ${event.name}`}</h3>
        <button className="btn-outline" onClick={onClose}><X size={14} /></button>
      </div>

      {!isNew && (
        <div className="row" style={{ gap: 0, marginTop: 12, borderBottom: "1px solid var(--card-border)" }}>
          {[
            ["details",    "Details"],
            ["recipients", `Recipients (${recipients.length})`],
            ["test",       "Test Send"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className="btn-outline"
              style={{
                borderRadius: 0,
                border: "none",
                borderBottom: tab === k ? "3px solid #3b82f6" : "3px solid transparent",
                fontWeight: tab === k ? 800 : 500,
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {err && <div className="card" style={{ marginTop: 10, color: "#dc2626" }}>{err}</div>}

      {/* ── Details ────────────────────────────────────────────────────────── */}
      {(isNew || tab === "details") && (
        <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
          {isNew && knownKeys.length > 0 && (
            <div>
              <label className="muted" style={{ fontSize: 12 }}>Start from a known event key:</label>
              <select
                className="input"
                style={{ width: "100%" }}
                value={eventKey}
                onChange={(e) => selectKnownKey(e.target.value)}
              >
                <option value="">— custom key —</option>
                {knownKeys.map(k => (
                  <option key={k.event_key} value={k.event_key}>{k.name} ({k.event_key})</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="muted" style={{ fontSize: 12 }}>event_key (stable identifier, used in code):</label>
            <input className="input" style={{ width: "100%" }} value={eventKey}
              onChange={e => setEventKey(e.target.value)} disabled={!isNew} />
          </div>
          <div>
            <label className="muted" style={{ fontSize: 12 }}>Name (shown in UI):</label>
            <input className="input" style={{ width: "100%" }} value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div>
            <label className="muted" style={{ fontSize: 12 }}>Description:</label>
            <input className="input" style={{ width: "100%" }} value={description} onChange={e => setDescription(e.target.value)} />
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
            <span style={{ fontWeight: 700 }}>Enabled (will fire on event)</span>
          </label>

          <div>
            <label className="muted" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>Send mode</label>
            <div style={{ display: "grid", gap: 6 }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: 10, border: `1px solid ${sendMode === "group" ? "#3b82f6" : "var(--card-border)"}`, borderRadius: 8, cursor: "pointer", background: sendMode === "group" ? "rgba(59,130,246,0.06)" : "transparent" }}>
                <input type="radio" name="send_mode" value="group" checked={sendMode === "group"} onChange={() => setSendMode("group")} style={{ marginTop: 2 }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>Group — single email to all recipients</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                    Everyone is in the To: line. Reply-all reaches the whole group. Use this when the notification should start a conversation (ops alerts, breakdowns, move assignments).
                  </div>
                </div>
              </label>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: 10, border: `1px solid ${sendMode === "individual" ? "#3b82f6" : "var(--card-border)"}`, borderRadius: 8, cursor: "pointer", background: sendMode === "individual" ? "rgba(59,130,246,0.06)" : "transparent" }}>
                <input type="radio" name="send_mode" value="individual" checked={sendMode === "individual"} onChange={() => setSendMode("individual")} style={{ marginTop: 2 }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>Individual — one email per recipient</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                    Each recipient sees only themselves. Use for customer-facing or privacy-sensitive notifications where recipients should not see each other.
                  </div>
                </div>
              </label>
            </div>
          </div>
          <div>
            <label className="muted" style={{ fontSize: 12 }}>Subject template:</label>
            <input
              className="input" style={{ width: "100%", fontFamily: "monospace" }}
              value={subjectTpl} onChange={e => setSubjectTpl(e.target.value)}
              placeholder="Unit {{unit.rowo}} picked up"
            />
          </div>
          <div>
            <label className="muted" style={{ fontSize: 12 }}>Plain-text body template (fallback):</label>
            <textarea
              className="input" style={{ width: "100%", minHeight: 120, fontFamily: "monospace" }}
              value={bodyTpl} onChange={e => setBodyTpl(e.target.value)}
              placeholder={"Hello,\n\nUnit {{unit.rowo}} has been picked up by {{driver.name}}."}
            />
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              Placeholders: <code>{"{{path.to.value}}"}</code>. Conditionals: <code>{"{{#if unit.rowo}}…{{/if}}"}</code>.
              Common paths: <code>{"{{unit.rowo}}"}</code>, <code>{"{{unit.customer}}"}</code>, <code>{"{{driver.name}}"}</code>,
              <code>{"{{move.TripId}}"}</code>, <code>{"{{session.id}}"}</code>.
            </div>
          </div>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <label className="muted" style={{ fontSize: 12 }}>HTML body template (optional, rich email):</label>
              <div className="row" style={{ gap: 6 }}>
                <button type="button" className="btn-outline" onClick={loadDefaults} disabled={!eventKey}
                  title="Replace subject/text/HTML with the built-in defaults for this event_key">
                  Load Defaults
                </button>
                <button type="button" className="btn-outline" onClick={runPreview} disabled={!htmlTpl.trim()}>
                  Preview
                </button>
              </div>
            </div>
            <textarea
              className="input" style={{ width: "100%", minHeight: 180, fontFamily: "monospace", fontSize: 12 }}
              value={htmlTpl} onChange={e => setHtmlTpl(e.target.value)}
              placeholder="<div>...</div> — leave empty to send plain text only"
            />
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              Inline styles only (most email clients ignore &lt;style&gt; tags). Same <code>{"{{...}}"}</code> +
              <code>{"{{#if}}"}</code> syntax as the text body.
            </div>
          </div>

          <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
            <button className="btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn" onClick={save} disabled={busy}>{isNew ? "Create" : "Save"}</button>
          </div>

          {showPreview && (
            <Modal onClose={() => setShowPreview(false)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <h3 style={{ margin: 0 }}>HTML Preview (sample data)</h3>
                <button className="btn-outline" onClick={() => setShowPreview(false)}><X size={14} /></button>
              </div>
              <iframe
                title="html-preview"
                srcDoc={htmlPreview}
                style={{ width: "100%", height: 560, border: "1px solid var(--card-border)", borderRadius: 6, background: "#fff" }}
              />
            </Modal>
          )}
        </div>
      )}

      {/* ── Recipients ─────────────────────────────────────────────────────── */}
      {!isNew && tab === "recipients" && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: "grid", gap: 6, marginBottom: 12 }}>
            {recipients.length === 0 && <div className="muted">No recipients yet.</div>}
            {recipients.map(r => (
              <div key={r.id} className="card" style={{ padding: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <code style={{ fontSize: 11, opacity: 0.6 }}>{r.recipient_type}</code>
                  <span style={{ fontWeight: 700, marginLeft: 8 }}>{r.recipient_value}</span>
                  {r.label && <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>({r.label})</span>}
                </div>
                <button className="btn-outline" onClick={() => removeRecipient(r.id)} style={{ color: "#dc2626" }}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>

          <div className="card" style={{ padding: 12, background: "rgba(255,255,255,0.02)" }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Add Recipient</div>
            <div style={{ display: "grid", gap: 8 }}>
              <select className="input" value={recType} onChange={e => setRecType(e.target.value)}>
                <option value="email">Specific Email Address</option>
                <option value="driver_id">Driver (by ID — uses their Email column)</option>
              </select>
              {recType === "email" ? (
                <input className="input" placeholder="someone@example.com"
                  value={recVal} onChange={e => setRecVal(e.target.value)} />
              ) : (
                <select className="input" value={recVal} onChange={e => setRecVal(e.target.value)}>
                  <option value="">— select driver —</option>
                  {drivers.map(d => (
                    <option key={d.DriverId} value={d.DriverId}>
                      {d.FullName} {d.Email ? `(${d.Email})` : "(no email)"}
                    </option>
                  ))}
                </select>
              )}
              <input className="input" placeholder="Label (optional)"
                value={recLabel} onChange={e => setRecLabel(e.target.value)} />
              <button className="btn" onClick={addRecipient} disabled={!recVal.trim()}>
                <Plus size={14} style={{ marginRight: 4, verticalAlign: -2 }} /> Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Test Send ──────────────────────────────────────────────────────── */}
      {!isNew && tab === "test" && (
        <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
          <div className="muted" style={{ fontSize: 13 }}>
            Renders this event's templates with sample data and sends. Leave "Override to" blank to fan out
            to all configured recipients.
          </div>
          <input
            className="input" placeholder="Override to (optional, e.g. your@email.com)"
            value={testTo} onChange={e => setTestTo(e.target.value)}
          />
          <button className="btn" onClick={runTest}>
            <Send size={14} style={{ marginRight: 4, verticalAlign: -2 }} /> Send Test
          </button>
          {testResult && (
            <pre className="card" style={{
              padding: 10, fontSize: 12, whiteSpace: "pre-wrap",
              background: testResult.ok ? "#f0fdf4" : "#fef2f2",
              color: testResult.ok ? "#15803d" : "#b91c1c",
            }}>
              {JSON.stringify(testResult, null, 2)}
            </pre>
          )}
        </div>
      )}
    </Modal>
  );
}
